import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Network } from '@sbclite/core';
import { api } from '../services/api';

// SIP Interface types matching backend schema
export interface TlsConfig {
  certificateId: string;
  caCertificateId?: string;
  verifyClient?: boolean;
  requireClientCert?: boolean;
  requestCert?: boolean;
  rejectUnauthorized?: boolean;
  minVersion?: 'TLSv1.2' | 'TLSv1.3';
  maxVersion?: 'TLSv1.2' | 'TLSv1.3';
  ciphers?: string[];
  honorCipherOrder?: boolean;
  teamsCompatible?: boolean;
  ecdhCurve?: string;
  dhparam?: string;
  sessionIdContext?: string;
  sessionTimeout?: number;
  allowInsecureRenegotiation?: boolean;
  enableTrace?: boolean;
}

// RFC 3581 NAT traversal configuration (symmetric response routing).
export interface NatTraversalConfig {
  mode: 'auto' | 'always' | 'never';
  forceRport?: boolean;
}

export type SipInterface = Network.SipInterfaceInfo;
export type SipInterfaceUsage = Network.SipInterfaceUsageInfo;

export interface SipInterfaceCreate {
  interfaceId: string;
  port: number;
  protocol: 'UDP' | 'TCP' | 'TLS';
  bindAddress?: string;
  advertisedAddress?: string | null;
  hostId?: string | null;
  enabled?: boolean;
  tlsConfig?: TlsConfig | null;
  preClassificationManipulationId?: string | null;
  natTraversalConfig?: NatTraversalConfig | null;
  challengeUnmatchedOptions?: boolean;
  maxConnections?: number | null;
  maxConcurrentCalls?: number | null;
  description?: string | null;
  tags?: string[] | null;
}

export interface SipInterfaceUpdate extends Partial<SipInterfaceCreate> {}

/**
 * Hook to fetch all SIP interfaces
 */
export function useSipInterfaces() {
  return useQuery({
    queryKey: ['sip-interfaces'],
    queryFn: async () => {
      const response = await api.getSipInterfaces();
      return response.data as SipInterface[];
    },
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch a single SIP interface by ID
 */
export function useSipInterface(id: string) {
  return useQuery({
    queryKey: ['sip-interfaces', id],
    queryFn: async () => {
      const response = await api.getSipInterface(id);
      return response.data as SipInterface;
    },
    enabled: !!id,
  });
}

/**
 * Hook to create a SIP interface
 */
export function useCreateSipInterface() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: SipInterfaceCreate) => {
      const response = await api.createSipInterface(data);
      return response.data as SipInterface;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-interfaces'] });
    },
  });
}

/**
 * Hook to update a SIP interface
 */
export function useUpdateSipInterface() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SipInterfaceUpdate }) => {
      const response = await api.updateSipInterface(id, data);
      return response.data as SipInterface;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['sip-interfaces'] });
      queryClient.invalidateQueries({ queryKey: ['sip-interfaces', id] });
    },
  });
}

/**
 * Hook to delete a SIP interface
 */
export function useDeleteSipInterface() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.deleteSipInterface(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-interfaces'] });
    },
  });
}

/**
 * Hook to fetch usage for a SIP interface
 */
export function useSipInterfaceUsage(id: string, enabled = true) {
  return useQuery({
    queryKey: ['sip-interfaces', id, 'usage'],
    queryFn: async () => {
      const response = await api.getSipInterfaceUsage(id);
      return response.data;
    },
    enabled: enabled && !!id,
  });
}
